<template>
  <div class="signatureEditor">
    <!-- Toolbar -->
    <div v-if="editor" class="toolbar">
      <button type="button" class="toolBtn" :class="{ active: editor.isActive('bold') }" @click="editor.chain().focus().toggleBold().run()" title="Bold">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M6 4h8a4 4 0 0 1 2.82 6.83A4 4 0 0 1 15 20H6V4zm2 2v5h6a2 2 0 1 0 0-4H8zm0 7v5h7a2 2 0 1 0 0-4H8z"/></svg>
      </button>
      <button type="button" class="toolBtn" :class="{ active: editor.isActive('italic') }" @click="editor.chain().focus().toggleItalic().run()" title="Italic">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M15 20H7v-2h2.93l2.14-12H10V4h8v2h-2.93l-2.14 12H15v2z"/></svg>
      </button>
      <button type="button" class="toolBtn" :class="{ active: editor.isActive('underline') }" @click="editor.chain().focus().toggleUnderline().run()" title="Underline">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8 3v9a4 4 0 0 0 8 0V3h2v9a6 6 0 0 1-12 0V3h2zM4 20h16v2H4v-2z"/></svg>
      </button>

      <span class="toolSep" />

      <button type="button" class="toolBtn" @click="openLinkDialog" title="Insert link">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
      </button>

      <span class="toolSep" />

      <label class="toolBtn colorLabel" title="Text color">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M11 2 5.5 16h2.25l1.12-3h6.25l1.13 3h2.25L13 2h-2zm-1.38 9L12 4.67 14.38 11H9.62z"/></svg>
        <input type="color" class="colorInput" @input="(e) => editor.chain().focus().setColor((e.target as HTMLInputElement).value).run()" />
      </label>

      <span class="toolSep" />

      <button type="button" class="toolBtn" :class="{ active: editor.isActive({ textAlign: 'left' }) }" @click="editor.chain().focus().setTextAlign('left').run()" title="Align left">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M3 3h18v2H3V3zm0 4h12v2H3V7zm0 4h18v2H3v-2zm0 4h12v2H3v-2zm0 4h18v2H3v-2z"/></svg>
      </button>
      <button type="button" class="toolBtn" :class="{ active: editor.isActive({ textAlign: 'center' }) }" @click="editor.chain().focus().setTextAlign('center').run()" title="Align center">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M3 3h18v2H3V3zm3 4h12v2H6V7zm-3 4h18v2H3v-2zm3 4h12v2H6v-2zm-3 4h18v2H3v-2z"/></svg>
      </button>
      <button type="button" class="toolBtn" :class="{ active: editor.isActive({ textAlign: 'right' }) }" @click="editor.chain().focus().setTextAlign('right').run()" title="Align right">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M3 3h18v2H3V3zm6 4h12v2H9V7zm-6 4h18v2H3v-2zm6 4h12v2H9v-2zm-6 4h18v2H3v-2z"/></svg>
      </button>

      <span class="toolSep" />

      <!-- Image upload hidden until S3 image hosting lands -->
      <!--
      <button type="button" class="toolBtn" @click="insertImage" title="Insert image">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
      </button>
      -->
    </div>

    <!-- Editor content -->
    <EditorContent :editor="editor" class="editorContent" />

    <!-- Hidden file input for image upload -->
    <input
      ref="fileInput"
      type="file"
      accept="image/png,image/jpeg,image/gif,image/bmp,image/svg+xml"
      style="display:none;"
      @change="onFileSelected"
    />

    <!-- Link dialog -->
    <div v-if="linkDialogOpen" class="linkDialogBackdrop" @click.self="closeLinkDialog">
      <div class="linkDialog">
        <div class="linkDialogTitle">Insert link</div>
        <div class="field">
          <label>Text</label>
          <input class="input" v-model="linkText" placeholder="Link text" :readonly="linkHasSelection" :style="linkHasSelection ? 'opacity:0.6;' : ''" @keydown.enter="applyLink" />
        </div>
        <div class="field">
          <label>URL</label>
          <input class="input" v-model="linkUrl" placeholder="https://..." @keydown.enter="applyLink" />
        </div>
        <div class="linkDialogActions">
          <button class="btn secondary btn-sm" type="button" @click="closeLinkDialog">Cancel</button>
          <button class="btn btn-sm" type="button" @click="applyLink" :disabled="!linkUrl.trim()">Apply</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount } from 'vue'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Paragraph from '@tiptap/extension-paragraph'
import HardBreak from '@tiptap/extension-hard-break'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import TextStyle from '@tiptap/extension-text-style'
import Color from '@tiptap/extension-color'
import TextAlign from '@tiptap/extension-text-align'
import Image from '@tiptap/extension-image'
import { VueNodeViewRenderer } from '@tiptap/vue-3'
import ResizableImageView from './ResizableImageView.vue'
import { useToast } from '../composables/useToast'

const { show: showToast } = useToast()

const ResizableImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: { default: null, renderHTML: (attrs) => attrs.width ? { width: attrs.width } : {} },
      height: { default: null, renderHTML: (attrs) => attrs.height ? { height: attrs.height } : {} },
    }
  },
  addNodeView() {
    return VueNodeViewRenderer(ResizableImageView as any)
  },
})

const MAX_IMAGE_SIZE = 500 * 1024 // 500KB

const props = defineProps<{
  modelValue: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const fileInput = ref<HTMLInputElement | null>(null)

// Link dialog state
const linkDialogOpen = ref(false)
const linkHasSelection = ref(false)
const linkText = ref('')
const linkUrl = ref('')

const editor = useEditor({
  content: props.modelValue || '',
  extensions: [
    StarterKit.configure({
      heading: { levels: [1, 2, 3] },
      // Use <br> for Enter instead of creating new <p> blocks.
      // Signatures should be compact — each line doesn't need its own paragraph.
      paragraph: false,
      hardBreak: false,
    }),
    // Re-add Paragraph but make Enter produce <br> inside it
    // by importing HardBreak with the keyboard shortcut mapped to Enter.
    Paragraph,
    HardBreak.extend({
      addKeyboardShortcuts() {
        return { Enter: () => this.editor.commands.setHardBreak() }
      },
    }),
    Underline,
    Link.configure({
      openOnClick: false,
      HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
    }),
    TextStyle,
    Color,
    TextAlign.configure({
      types: ['heading', 'paragraph'],
    }),
    ResizableImage.configure({
      inline: true,
      allowBase64: true,
    }),
  ],
  onUpdate({ editor: ed }) {
    emit('update:modelValue', ed.getHTML())
  },
  editorProps: {
    handleDrop(view, event) {
      const dt = event.dataTransfer
      if (!dt || dt.types.includes('application/x-prosemirror')) return false
      const hasImages = dt.files && dt.files.length > 0 && Array.from(dt.files).some(f => f.type.startsWith('image/'))
      if (hasImages) {
        event.preventDefault()
        showToast('Image support is coming soon.', 3000)
        return true
      }
      return false
    },
    handlePaste(view, event) {
      const items = event.clipboardData?.items
      if (items) {
        for (const item of Array.from(items)) {
          if (item.type.startsWith('image/')) {
            event.preventDefault()
            showToast('Image support is coming soon.', 3000)
            return true
          }
        }
      }
      return false
    },
  },
})

watch(() => props.modelValue, (val) => {
  if (editor.value && editor.value.getHTML() !== val) {
    editor.value.commands.setContent(val || '', false)
  }
})

onBeforeUnmount(() => {
  editor.value?.destroy()
})

function openLinkDialog() {
  const { from, to } = editor.value!.state.selection
  const selectedText = editor.value!.state.doc.textBetween(from, to, '')
  linkHasSelection.value = from !== to
  linkText.value = selectedText || ''
  linkUrl.value = editor.value?.getAttributes('link')?.href || ''
  linkDialogOpen.value = true
}

function closeLinkDialog() {
  linkDialogOpen.value = false
  linkText.value = ''
  linkUrl.value = ''
}

function applyLink() {
  const url = linkUrl.value.trim()
  if (!url) return
  const text = linkText.value.trim() || url

  if (editor.value) {
    const { from, to } = editor.value.state.selection
    if (from === to && !editor.value.state.doc.textBetween(from, to, '')) {
      // No selection — insert text with link
      editor.value.chain().focus().insertContent(`<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`).run()
    } else {
      // Selection exists — wrap it in a link
      editor.value.chain().focus().setLink({ href: url }).run()
    }
  }
  closeLinkDialog()
}

function insertImage() {
  fileInput.value?.click()
}

function onFileSelected(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (file) {
    embedImageFile(file)
  }
  input.value = ''
}

function embedImageFile(file: File) {
  if (file.size > MAX_IMAGE_SIZE) {
    alert(`Image is too large (${Math.round(file.size / 1024)}KB). Maximum is 500KB.`)
    return
  }
  const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/bmp', 'image/svg+xml']
  if (!allowedTypes.includes(file.type)) {
    alert('Unsupported image format. Use PNG, JPEG, GIF, BMP, or SVG.')
    return
  }
  const reader = new FileReader()
  reader.onload = () => {
    const dataUrl = reader.result as string
    editor.value?.chain().focus().setImage({ src: dataUrl }).run()
  }
  reader.readAsDataURL(file)
}
</script>

<style scoped>
.signatureEditor {
  border: 1px solid var(--border);
  border-radius: var(--radius-md, 8px);
  overflow: hidden;
  position: relative;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: var(--space-1, 4px) var(--space-2, 8px);
  border-bottom: 1px solid var(--border);
  background: var(--color-bg-subtle, var(--bg-subtle, #f5f5f5));
  flex-wrap: wrap;
}

.toolBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: var(--radius-sm, 6px);
  background: transparent;
  cursor: pointer;
  color: var(--color-text-secondary, var(--muted));
  position: relative;
  transition: background var(--duration-fast, 120ms) ease, color var(--duration-fast, 120ms) ease;
}
.toolBtn:hover { background: var(--color-bg-sunk, var(--bg-hover, #e8e8e8)); color: var(--color-text-primary, var(--text)); }
.toolBtn.active { background: var(--color-bg-sunk, var(--bg-hover, #e0e0e0)); color: var(--color-text-primary, var(--text)); }

.toolSep {
  width: 1px;
  height: 16px;
  background: var(--border);
  margin: 0 var(--space-1, 4px);
  flex-shrink: 0;
}

.colorLabel { overflow: hidden; }
.colorInput {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 4px;
  padding: 0;
  border: none;
  cursor: pointer;
  opacity: 0.7;
}

.editorContent {
  min-height: 120px;
  max-height: 400px;
  overflow-y: auto;
  padding: var(--space-3, 12px);
  background: #fff;
}
.editorContent :deep(.tiptap) {
  outline: none;
  min-height: 100px;
  font-family: var(--font-sans, sans-serif);
  font-size: var(--fs-sm, 13px);
  line-height: var(--lh-normal, 1.5);
  color: var(--color-text-primary, var(--text));
}
.editorContent :deep(.tiptap p) { margin: 0; }
.editorContent :deep(.tiptap img) {
  max-width: 100%;
  height: auto;
  cursor: pointer;
  border: 2px solid transparent;
  border-radius: 2px;
}
.editorContent :deep(.tiptap img.ProseMirror-selectednode) {
  border-color: var(--color-accent, var(--accent, #059669));
}
.editorContent :deep(.tiptap a) {
  color: var(--color-accent, var(--accent, #2563eb));
  text-decoration: underline;
}

/* ── Link dialog ─────────────────────────── */
.linkDialogBackdrop {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.25);
}
.linkDialog {
  background: var(--color-bg-surface, var(--surface, #fff));
  border: 1px solid var(--border);
  border-radius: var(--radius-lg, 12px);
  padding: var(--space-5, 20px);
  width: 360px;
  max-width: 90vw;
  display: flex;
  flex-direction: column;
  gap: var(--space-3, 12px);
  box-shadow: var(--shadow-lg, 0 8px 30px rgba(0,0,0,0.12));
}
.linkDialogTitle {
  font-family: var(--font-sans, sans-serif);
  font-size: var(--fs-base, 15px);
  font-weight: var(--fw-semibold, 600);
  color: var(--color-text-primary, var(--text));
}
.linkDialog .field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.linkDialog .field label {
  font-size: var(--fs-xs, 12px);
  font-weight: var(--fw-semibold, 600);
  color: var(--color-text-secondary, var(--muted));
}
.linkDialog .input {
  width: 100%;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm, 6px);
  padding: 8px 10px;
  font-size: var(--fs-sm, 13px);
  font-family: var(--font-sans, sans-serif);
}
.linkDialogActions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2, 8px);
  margin-top: var(--space-1, 4px);
}
</style>
